import { useQuery } from 'react-query';

import { useCompany } from 'modules/app/hooks/useCompany';
import { useTranslation } from 'src/core/common/hooks/useTranslation';
import appConfig from 'src/core/config';

import { type ApprovalWorkflow } from '../approvalWorkflow';
import { transformApprovalWorkflowToNodesAndEdges } from '../helpers/transformApprovalWorkflowToNodesAndEdges';

const fetchActiveApprovalWorkflow = async (companyId: string) =>
  fetch(`${appConfig.apiUrls.apiV2}/${companyId}/approval-workflows/active`, {
    method: 'GET',
    credentials: 'include',
    headers: {
      // eslint-disable-next-line @typescript-eslint/naming-convention
      'Content-Type': 'application/json',
    },
  });

export const useApprovalWorkflowQuery = () => {
  const company = useCompany();
  return useQuery(
    ['approval-workflows', 'active'],
    async (): Promise<ApprovalWorkflow> => {
      const response = await fetchActiveApprovalWorkflow(company.id);
      if (!response.ok) {
        const error = new Error(
          'Failed to fetch approval workflow',
        ) as Error & { status?: number };
        error.status = response.status;
        throw error;
      }
      return response.json();
    },
    {
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
};

export const useApprovalWorkflowHasNodeWithCostCenter = ({
  costCenterId,
}: {
  costCenterId: string;
}) => {
  const { t } = useTranslation('global');
  const approvalWorkflowQuery = useApprovalWorkflowQuery();
  if (approvalWorkflowQuery.status !== 'success') {
    return false;
  }
  const transformed = transformApprovalWorkflowToNodesAndEdges(
    approvalWorkflowQuery.data,
    t,
  );
  return transformed.nodes.some(
    (node) =>
      node.type === 'baseNode' &&
      node.data.dimension?.type === 'costCenter' &&
      node.data.values?.some((value) => value.id === costCenterId),
  );
};
